import {
    partnerCockpitValidationResponseSchema,
    type PartnerCockpitValidationResponse,
    type SavePartnerCockpitValidationInput,
    type ValidationDocumentType,
} from '@houzy/schemas/partner-management/partner-validation';
import { queryOptions, useMutation, useQueryClient, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { apiRequest, retryQueryUnlessClientError, type ApiContractError, type ApiError } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';
import { useTrackEvent } from '../../../lib/trackingEvents';
import { settingsMutationKey } from '../../settings/api/settingsApi';

export const validationQueryKey = ['validation'] as const;

export const validationQueryOptions = queryOptions({
    queryKey: validationQueryKey,
    queryFn: ({ signal }): Promise<PartnerCockpitValidationResponse> =>
        apiRequest(partnerCockpitApiPath('validation'), {
            signal,
            responseSchema: partnerCockpitValidationResponseSchema,
        }),
    staleTime: STALE_TIME_MS,
    retry: retryQueryUnlessClientError,
});

export function useValidation(): UseQueryResult<PartnerCockpitValidationResponse> {
    return useQuery(validationQueryOptions);
}

type ValidationMutation<TInput> = ReturnType<
    typeof useMutation<PartnerCockpitValidationResponse, ApiError | ApiContractError, TInput>
>;

// Every route answers with the whole validation, so a success can replace the cached copy outright.
function useValidationMutation<TInput>(
    request: (input: TInput) => Promise<PartnerCockpitValidationResponse>,
    onSaved?: () => void,
): ValidationMutation<TInput> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitValidationResponse, ApiError | ApiContractError, TInput>({
        mutationKey: [...settingsMutationKey, 'validation'],
        mutationFn: request,
        onSuccess: (validation: PartnerCockpitValidationResponse) => {
            queryClient.setQueryData(validationQueryKey, validation);
            onSaved?.();
        },
    });
}

export function useSaveValidationMutation(): ValidationMutation<SavePartnerCockpitValidationInput> {
    return useValidationMutation<SavePartnerCockpitValidationInput>((body) =>
        apiRequest(partnerCockpitApiPath('validation'), {
            method: 'PUT',
            body,
            responseSchema: partnerCockpitValidationResponseSchema,
        }),
    );
}

export type AddValidationDocumentInput = { type: ValidationDocumentType; file: File };

export function useAddValidationDocumentMutation(): ValidationMutation<AddValidationDocumentInput> {
    const track = useTrackEvent();
    return useValidationMutation<AddValidationDocumentInput>(
        ({ type, file }) => {
            const body = new FormData();
            body.append('document', file);
            body.append('type', type);
            return apiRequest(partnerCockpitApiPath('validation/documents'), {
                method: 'POST',
                body,
                responseSchema: partnerCockpitValidationResponseSchema,
            });
        },
        () => track('Verification Document Uploaded', {}),
    );
}

export function useDeleteValidationDocumentMutation(): ValidationMutation<string> {
    return useValidationMutation<string>((documentUuid) =>
        apiRequest(partnerCockpitApiPath(`validation/documents/${documentUuid}`), {
            method: 'DELETE',
            responseSchema: partnerCockpitValidationResponseSchema,
        }),
    );
}
